/**
 * Adjuntos de una entidad.
 *
 * Subir, listar, descargar y dar de baja los papeles de terceros: la póliza
 * de RC, el certificado de una habilitación. Una fecha de vencimiento sin el
 * documento detrás no acredita nada ante una inspección.
 *
 * El fichero se envía **en crudo**, con su propio `Content-Type`, no como
 * `multipart/form-data`: es un fichero, no un formulario. El cliente HTTP ya
 * añade el testigo CSRF y respeta la cabecera si viene puesta.
 */

import { useRef, useState, type ReactNode } from 'react';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';

export interface AttachmentItem {
  id: string;
  kind: string;
  filename: string;
  content_type: string;
  byte_size: string;
  created_at: string;
}

interface Props {
  title: string;
  note?: string;
  entityType: 'COMPANY' | 'COMPANY_AUTHORIZATION' | 'CUSTOMER';
  /** `null` mientras no ha cargado la entidad: el panel se muestra inerte. */
  entityId: string | null;
  kind: 'LIABILITY_POLICY' | 'AUTHORIZATION_CERTIFICATE' | 'LOGO' | 'LOGO_MONO' | 'PRL' | 'OTHER';
  items: AttachmentItem[];
  onChanged: () => void;
}

/** Lo que acepta el servidor. Se repite aquí para avisar antes de subir. */
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/heic';
const MAX_BYTES = 25 * 1024 * 1024;

export function AttachmentsPanel({
  title,
  note,
  entityType,
  entityId,
  kind,
  items,
  onChanged,
}: Props): ReactNode {
  const { api } = useSession();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File): Promise<void> {
    setError(null);

    // Se comprueba aquí además de en el servidor. No es por seguridad —esa
    // la decide el servidor— sino por no hacerle subir 30 MB a alguien para
    // decirle al final que no caben.
    if (file.size > MAX_BYTES) {
      setError(`«${file.name}» ocupa ${Math.round(file.size / 1024 / 1024)} MB y el máximo son 25 MB.`);
      return;
    }
    if (!ACCEPT.split(',').includes(file.type)) {
      setError(`«${file.name}» es de tipo ${file.type || 'desconocido'}. Se aceptan PDF, JPEG, PNG y HEIC.`);
      return;
    }

    setBusy(true);
    try {
      const query = new URLSearchParams({ filename: file.name, kind });
      await api.request<{ id: string }>(
        `/attachments/${entityType}/${entityId!}?${query.toString()}`,
        { method: 'POST', body: file, headers: { 'content-type': file.type } },
      );
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido subir el fichero.');
    } finally {
      setBusy(false);
      // Se limpia para poder volver a elegir el mismo fichero si hizo falta
      // corregir algo y reintentar.
      if (input.current) input.current.value = '';
    }
  }

  async function remove(id: string, filename: string): Promise<void> {
    if (!globalThis.confirm(`¿Dar de baja «${filename}»?\n\nSeguirá guardado como prueba de lo que hubo, pero dejará de aparecer aquí.`)) {
      return;
    }

    setError(null);
    setBusy(true);
    try {
      await api.delete(`/attachments/file/${id}`);
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido dar de baja.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h3 className="panel__title">{title}</h3>
      {note !== undefined && <p className="screen__note">{note}</p>}

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      {items.length === 0 ? (
        <p className="screen__note">No hay ningún documento subido.</p>
      ) : (
        <ul className="attachments">
          {items.map((item) => (
            <li className="attachments__item" key={item.id}>
              {/* Enlace normal y no `fetch`: la descarga la resuelve el
                  navegador, las cookies viajan solas y un GET no necesita
                  testigo CSRF. */}
              <a
                className="attachments__name"
                href={`/v1/attachments/file/${item.id}`}
                download={item.filename}
              >
                {item.filename}
              </a>
              <span className="attachments__meta">
                {formatSize(item.byte_size)} · {formatDate(item.created_at)}
              </span>
              <button
                className="button button--quiet"
                type="button"
                disabled={busy}
                onClick={() => void remove(item.id, item.filename)}
              >
                Dar de baja
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={input}
        className="attachments__input"
        type="file"
        accept={ACCEPT}
        disabled={busy || entityId === null}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void upload(file);
        }}
      />
      {busy && <p className="screen__note">Subiendo…</p>}
    </section>
  );
}

function formatSize(bytes: string): string {
  const n = Number(bytes);
  if (!Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('es-ES');
}
